import React, { useEffect } from 'react'
import { connect, useDispatch } from 'react-redux'

import { setFullScreen } from '@store/actionSlices/appConfig'
import { RouteKey } from '@store/constants'
import { ProjectIdentity, RootStateTypeExtra } from '@store/types'

import usePropertyCompareFirebaseTrigger from '@components/compare-property/utils'

import FirebaseControlQuery from '@firebaseUtil/firebase-control-query'
import { SessionMap } from '@firebaseUtil/types'

import { ArrowSvg } from '@svg/react'

interface ComponentProps {
  session: SessionMap | undefined
  projectIdentity: ProjectIdentity
  className?: string
  iconClasses?: string
  onOverrideClick?: () => void
}

function BackButton({
  session,
  projectIdentity,
  className = 'rounded-full border border-zinc-400 p-1',
  iconClasses = 'h-6 w-6 stroke-zinc-400',
  onOverrideClick,
}: ComponentProps) {
  const dispatch = useDispatch()

  const firebaseControlQuery = FirebaseControlQuery({
    ...projectIdentity,
    sessionKey: projectIdentity.sessionId,
  })

  const { triggerComparisonStop, isActive } =
    usePropertyCompareFirebaseTrigger()

  const [activeLevel, setActiveLevel] = React.useState<string>('')
  const [activeUnit, setActiveUnit] = React.useState<string>('')

  const isReturningToThreeDLevel =
    !!projectIdentity?.showcase3DIntegration &&
    !!session?.threeDView?.selectedLevelId

  const handleBackNav = async () => {
    if (isActive) {
      triggerComparisonStop()
      firebaseControlQuery.update({
        [`building.activeUnit`]: '',
      })
      return
    }
    if (activeUnit) {
      await firebaseControlQuery.update({
        [`building.activeUnit`]: '',
      })
      if (!isReturningToThreeDLevel) {
        return
      }
    }

    if (activeLevel) {
      await firebaseControlQuery.update({
        [`building.activeLevel`]: '',
        [`building.activeUnit`]: '',
      })

      dispatch(setFullScreen(false))

      if (isReturningToThreeDLevel) {
        await firebaseControlQuery.updateRoute(RouteKey.ADTHREED)
        return
      }

      if (projectIdentity?.isSnaploaderActive) {
        await firebaseControlQuery.updateRoute(RouteKey.SNAPLOADER_VIEW)
        return
      }
      if (projectIdentity?.isEnvisionVRActive) {
        await firebaseControlQuery.updateRoute(RouteKey.THREE_D_BUILDING)
        return
      }
      if (projectIdentity?.hasThreeDViewer) {
        await firebaseControlQuery.updateRoute('areaview-3d')
        return
      }
      if (projectIdentity?.showcase3DIntegration) {
        await firebaseControlQuery.updateRoute(RouteKey.ADTHREED)
        return
      }

      await firebaseControlQuery.updateRoute(RouteKey.LEVEL_VIEW)
    }
  }

  useEffect(() => {
    if (!session) {
      return
    }

    const {
      building: { activeLevel, activeUnit },
    } = session

    setActiveLevel(activeLevel)
    setActiveUnit(activeUnit)
  }, [session])

  return (
    <button
      type="button"
      className={className}
      onClick={onOverrideClick ?? handleBackNav}
    >
      <ArrowSvg className={iconClasses} rotate="left" />
    </button>
  )
}

export default connect(
  ({ projectIdentity, firestore: { session } }: RootStateTypeExtra) => ({
    session,
    projectIdentity,
  })
)(BackButton)
